import { Head, router, usePage } from '@inertiajs/react'
import { useState } from 'react'
import type {
  AssessmentReport,
  AssessmentView,
  ReportCriterion,
  SubmissionView,
  TranscriptReview,
} from '@/types/assessment'
import type { SharedPageProps } from '@/types/shared'
import { CandidateNav } from '@/components/Shared/CandidateNav'
import { FlashBanner } from '@/components/Shared/FlashBanner'
import { useScanPolling } from '@/hooks/useScanPolling'

interface AssessmentShowProps {
  assessment: AssessmentView | null
  eligible: boolean
}

function formatGeneratedDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function startAssessment() {
  router.post('/assessment')
}

function IntroCard() {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <div className="mb-3 flex items-center gap-[10px]">
        <span className="font-mono text-[11px] uppercase tracking-eyebrow text-primary">
          // Hyrin assessment
        </span>
        <span className="rounded-[5px] bg-secondary px-2 py-[4px] font-mono text-[10px] font-bold uppercase tracking-[0.06em] text-primary">
          1 free / account
        </span>
      </div>
      <h1 className="mb-3 font-heading text-[28px] font-bold leading-[1.1] tracking-[-0.02em] text-foreground">
        Take the Hyrin assessment
      </h1>
      <p className="mb-6 max-w-[560px] font-sans text-[15px] leading-[1.5] text-muted-foreground">
        A ~45-minute personalized build challenge that turns your claimed skills into assessed
        proof. The brief is generated from your own proof trail — it targets the skills you claim
        but haven&apos;t yet backed with evidence.
      </p>
      <button
        onClick={startAssessment}
        className="inline-flex h-[46px] items-center rounded-cta bg-primary px-[22px] font-sans text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90"
      >
        Start free assessment
      </button>
      <p className="mt-[9px] font-mono text-[11px] text-muted-foreground">
        ~45 min · one attempt included
      </p>
    </div>
  )
}

function NotEligibleCard() {
  return (
    <div className="rounded-card border border-dashed border-border p-7">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
        Hyrin assessment
      </p>
      <h1 className="mb-3 font-heading text-[24px] font-bold leading-[1.15] tracking-[-0.01em] text-muted-foreground">
        Not available yet
      </h1>
      <p className="max-w-[560px] font-sans text-[14px] leading-[1.5] text-muted-foreground">
        The assessment is personalized from your analyzed proof. It opens once you have connected a
        source through your agent, your evidence has been analyzed, and there are claimed or
        shallow skills left to probe.
      </p>
      <a href="/connect" className="mt-4 inline-block font-mono text-[12px] text-primary">
        Connect a source →
      </a>
    </div>
  )
}

function GeneratingCard({ assessment }: { assessment: AssessmentView }) {
  const [startedAtMs] = useState(() => Date.now())
  const { timedOut } = useScanPolling({
    active: true,
    startedAtMs,
    reloadKeys: ['assessment'],
  })

  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
        // Generating
      </p>
      <h1 className="mb-3 font-heading text-[24px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
        Writing your personalized brief
      </h1>
      <p className="max-w-[560px] font-sans text-[14px] leading-[1.5] text-muted-foreground">
        Hyrin is composing a build challenge from your proof trail — this usually takes under a
        minute.
      </p>
      {timedOut ? (
        <button
          onClick={() => router.reload({ only: ['assessment'] })}
          className="mt-5 h-[38px] rounded-[9px] border border-foreground bg-background px-4 font-sans text-[13px] font-semibold text-foreground hover:bg-muted"
        >
          Still working — check again
        </button>
      ) : (
        <p className="mt-5 font-mono text-[11px] text-muted-foreground">
          requested {formatGeneratedDate(assessment.generatedAt)} · refreshing automatically…
        </p>
      )}
    </div>
  )
}

function ReadyBrief({ assessment }: { assessment: AssessmentView }) {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[11px] uppercase tracking-eyebrow text-primary">
          // Your assessment brief
        </p>
        <p className="font-mono text-[11px] text-muted-foreground">
          generated {formatGeneratedDate(assessment.generatedAt)}
        </p>
      </div>
      <h1 className="mb-4 font-heading text-[24px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
        Take the Hyrin assessment
      </h1>
      <pre className="whitespace-pre-wrap font-sans text-[15px] leading-[1.6] text-foreground">
        {assessment.brief}
      </pre>
      <p className="mt-6 border-t border-border pt-4 font-mono text-[11px] text-muted-foreground">
        {assessment.submission
          ? '~45 min · submitted through your agent'
          : '~45 min · work with your coding agent · submit through your agent: get_assessment → submit_assessment'}
      </p>
    </div>
  )
}

function GradingInProgressCard() {
  const [startedAtMs] = useState(() => Date.now())
  const { timedOut } = useScanPolling({
    active: true,
    startedAtMs,
    reloadKeys: ['assessment'],
  })

  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
        // Grading
      </p>
      <h2 className="mb-3 font-heading text-[22px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
        Grading your submission
      </h2>
      <p className="max-w-[560px] font-sans text-[14px] leading-[1.5] text-muted-foreground">
        Hyrin is grading your submission criterion by criterion against the brief&apos;s rubric —
        this usually takes under a minute.
      </p>
      {timedOut ? (
        <button
          onClick={() => router.reload({ only: ['assessment'] })}
          className="mt-5 h-[38px] rounded-[9px] border border-foreground bg-background px-4 font-sans text-[13px] font-semibold text-foreground hover:bg-muted"
        >
          Still working — check again
        </button>
      ) : (
        <p className="mt-5 font-mono text-[11px] text-muted-foreground">
          submission received · refreshing automatically…
        </p>
      )}
    </div>
  )
}

function GradingFailedCard() {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-destructive">
        Grading failed
      </p>
      <h2 className="mb-3 font-heading text-[22px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
        Grading hit a problem
      </h2>
      <p className="max-w-[560px] font-sans text-[14px] leading-[1.5] text-muted-foreground">
        Your submission is safe. Ask your agent to resubmit the same bundle to retry, or the team
        can re-run it.
      </p>
    </div>
  )
}

function CriterionCard({ criterion }: { criterion: ReportCriterion }) {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-heading text-[18px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
          {criterion.name}
        </h3>
        <span className="font-mono text-[13px] font-bold text-primary">{criterion.score_0_5}/5</span>
      </div>
      <p className="mb-4 border-l-2 border-primary pl-3 font-sans text-[13px] leading-[1.5] text-muted-foreground">
        {criterion.anchor}
      </p>
      <p className="mb-1 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
        Evidence
      </p>
      <ul className="mb-4 space-y-1">
        {criterion.evidence.map((line) => (
          <li key={line} className="font-sans text-[13px] leading-[1.5] text-muted-foreground">
            {line}
          </li>
        ))}
      </ul>
      <p className="font-sans text-[14px] leading-[1.5] text-foreground">{criterion.finding}</p>
      <div className="mt-4 rounded-[9px] border-l-2 border-primary bg-muted px-4 py-3">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-primary">
          Next step
        </p>
        <p className="font-sans text-[13px] leading-[1.5] text-foreground">
          {criterion.improvement_action}
        </p>
      </div>
    </div>
  )
}

const TRANSCRIPT_REVIEW_ROWS: { key: keyof TranscriptReview, label: string }[] = [
  { key: 'framing', label: 'Framing' },
  { key: 'decomposition', label: 'Decomposition' },
  { key: 'verification', label: 'Verification' },
  { key: 'agent_correction', label: 'Agent correction' },
]

function TranscriptReviewCard({ review }: { review: TranscriptReview }) {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-4 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
        // Transcript review
      </p>
      <dl className="space-y-4">
        {TRANSCRIPT_REVIEW_ROWS.map(({ key, label }) => (
          <div key={key} className="border-t border-border pt-4 first:border-t-0 first:pt-0">
            <dt className="mb-1 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
              {label}
            </dt>
            <dd className="font-sans text-[14px] leading-[1.5] text-foreground">{review[key]}</dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function ReportList({ title, items }: { title: string, items: string[] }) {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
        // {title}
      </p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item} className="flex gap-2 font-sans text-[13px] leading-[1.5] text-foreground">
            <span className="mt-[7px] size-[5px] shrink-0 rounded-full bg-primary" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function GradedReport({ report, gradedAt }: { report: AssessmentReport, gradedAt?: string }) {
  return (
    <div className="space-y-6">
      <div className="rounded-card border border-border bg-background p-7">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            // Assessment report
          </p>
          {gradedAt && (
            <p className="font-mono text-[11px] text-muted-foreground">
              graded {formatGeneratedDate(gradedAt)}
            </p>
          )}
        </div>
        <h2 className="mb-3 font-heading text-[24px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
          Your assessment report
        </h2>
        <p className="font-sans text-[15px] leading-[1.6] text-foreground">
          {report.overall_summary}
        </p>
      </div>
      {report.criteria.map((criterion) => (
        <CriterionCard key={criterion.name} criterion={criterion} />
      ))}
      <TranscriptReviewCard review={report.transcript_review} />
      <div className="grid gap-6 sm:grid-cols-2">
        <ReportList title="Strengths" items={report.strengths} />
        <ReportList title="Growth areas" items={report.growth_areas} />
      </div>
    </div>
  )
}

function SubmissionSection({ submission }: { submission: SubmissionView }) {
  if (submission.status === 'submitted') {
    return <GradingInProgressCard />
  }
  if (submission.status === 'graded' && submission.report) {
    return <GradedReport report={submission.report} gradedAt={submission.gradedAt} />
  }
  if (submission.status === 'grading_failed') {
    return <GradingFailedCard />
  }
  return null
}

function FailedCard() {
  return (
    <div className="rounded-card border border-border bg-background p-7">
      <p className="mb-2 font-mono text-[11px] uppercase tracking-eyebrow text-destructive">
        Generation failed
      </p>
      <h1 className="mb-3 font-heading text-[24px] font-bold leading-[1.15] tracking-[-0.01em] text-foreground">
        We couldn&apos;t generate your brief
      </h1>
      <p className="max-w-[560px] font-sans text-[14px] leading-[1.5] text-muted-foreground">
        Something went wrong while composing your assessment. Nothing was used up — your free
        attempt is still available.
      </p>
      <button
        onClick={startAssessment}
        className="mt-5 inline-flex h-[46px] items-center rounded-cta bg-primary px-[22px] font-sans text-[15px] font-semibold text-primary-foreground transition-opacity hover:opacity-90"
      >
        Try again
      </button>
    </div>
  )
}

export default function AssessmentShow({ assessment, eligible }: AssessmentShowProps) {
  const { flash } = usePage<SharedPageProps>().props

  return (
    <>
      <Head title="Assessment — Hyrin" />
      <div className="min-h-screen bg-muted">
        <CandidateNav />
        <FlashBanner flash={flash} />
        <main className="mx-auto max-w-[760px] px-5 py-10 sm:px-8">
          {!assessment && eligible && <IntroCard />}
          {!assessment && !eligible && <NotEligibleCard />}
          {assessment?.status === 'generating' && <GeneratingCard assessment={assessment} />}
          {assessment?.status === 'ready' && (
            <div className="space-y-6">
              <ReadyBrief assessment={assessment} />
              {assessment.submission && <SubmissionSection submission={assessment.submission} />}
            </div>
          )}
          {assessment?.status === 'failed' && <FailedCard />}
        </main>
      </div>
    </>
  )
}
